import type { CharacterEntry, LessonBundle } from '@ezishizi/content-schema';
import { effectiveLessonOrder } from './bands.ts';
import { CHALLENGE } from './config.ts';
import { buildGameRound, type GameId } from './games.ts';
import {
  buildModule,
  isWriteTarget,
  lessonEntries,
  makeQuestion,
  spreadChars,
  type BuildContext,
} from './questions.ts';
import { mulberry32, shuffle } from './rng.ts';
import type { ExerciseType, Module, Question } from './types.ts';

export { effectiveLessonOrder } from './bands.ts';

/**
 * Which minigame each stop of an island plays.
 *
 * The old island was seven stops that each drilled one skill with the plain question builders.
 * This picks a *game* for each stop instead, and it cannot simply name one: a format is not
 * available everywhere. Feed Shizi needs food in the set, Which scene needs authored contrastive
 * scenes, Count needs a numeral with a measure word. Built against all 71 authored P1 sets, the
 * spread runs from six formats that play on every one of them to four that play on 12 to 27. So a
 * stop names a *family* and the format is chosen per island from what that island can actually
 * build (`set.games` in the content index, computed at build time).
 *
 * Everything here is pure and seeded by the island's own index, so a child returning to island
 * four finds the game they left, and consecutive islands differ.
 */

/** The six things an island asks a child to do, in the order they walk them. `games.ts`'s
 * `GameFamily` is a different axis — how a format is played (choose, match, build) rather than
 * what the stop is for. */
export type IslandSlot = 'meet' | 'listen' | 'read' | 'pinyin' | 'write' | 'use';

export const ISLAND_SLOTS: readonly IslandSlot[] = [
  'meet',
  'listen',
  'read',
  'pinyin',
  'write',
  'use',
];

/**
 * The formats each family may choose from, best first.
 *
 * No listen, write or use format plays on every set, so those ladders can come back empty; the
 * stop then runs the plain skill module it replaced (`FALLBACK_MODULE`). That is deliberate — the
 * old structure is the guaranteed tail rather than dead code, and no island is ever short a stop.
 */
export const SLOT_LADDER: Record<IslandSlot, readonly GameId[]> = {
  meet: ['meet_compact', 'meet_ten'],
  listen: ['bubbles', 'forks', 'trio', 'feed'],
  read: ['find_all', 'match_pairs', 'sort_meaning', 'count'],
  pinyin: ['tone_paint', 'tone_slide', 'sort_sound'],
  write: ['reveal', 'flash', 'stroke_order', 'stroke_missing'],
  use: ['bridge', 'scene', 'build', 'builder'],
};

/** The module a stop runs when its slot has no playable game on this set. */
export const FALLBACK_MODULE: Record<IslandSlot, Module> = {
  meet: 'learn',
  listen: 'listen',
  read: 'read',
  pinyin: 'pinyin',
  write: 'write',
  use: 'practice',
};

/**
 * The lesson a format may first be chosen for. Formats not listed are open from lesson 1.
 *
 * Difficulty used to be controlled here alone, which left lessons 1–3 with the write and use
 * ladders empty and three quarters of those islands as plain question screens. Now the band holds
 * back only what is genuinely harder to read or reason about; early difficulty is a ramp inside
 * the board, keyed on `effectiveLessonOrder`: Reveal draws the outline for lessons 1–2 and fades
 * it at 3, Match pairs deals three pairs before lesson 3 and five after, Missing stroke is always
 * guided, Character builder adds a decoy part only from its second character, Find them all walks
 * its prompt from hanzi to hidden to pinyin, Tone painter goes arrows, trace, blind.
 *
 * So the formats a lesson 1–3 child can play are open from lesson 1: Reveal, Missing stroke,
 * Match pairs, Sort by sound, Character builder and Sort by meaning. Bridge, Flash and Stroke
 * order open at 4 (Bridge reads a sentence in two planks before lesson 4 and three from it, but
 * reading whole sentences waits for 4; Flash and Stroke order are recall and ordering of strokes).
 * Listen and build opens at 6.
 *
 * A band has to be checked against where the content can actually play the format, not set from
 * how hard it feels. Which scene only builds for lessons 1–5, so banding it at 8 meant it could
 * never be scheduled at all; Feed Shizi (3, 7, 9, 10, 12, 15) and Count (1, 3–5, 7–9, 14, 15)
 * were nearly as bad at 14. All three are simple, picture-answering boards — they were late only
 * because they are rare, which is backwards. Which scene has no band at all now: it only builds
 * for lessons 1–5, so any band costs it islands it could have had. Listen and build opens at 6,
 * where it first builds, not 8.
 *
 * `islands.test.ts` holds this to the content: a band may not start after the last lesson its
 * format can build for, so a mistake of this shape fails the suite rather than quietly emptying
 * a slot.
 */
export const UNLOCKED_FROM: Partial<Record<GameId, number>> = {
  bridge: 2,
  build: 6,
  flash: 4,
  stroke_order: 4,
};

/**
 * Formats that most islands cannot play: Which scene needs authored contrastive scenes, Feed
 * Shizi food in the set, Count a numeral with a measure word, Listen and build a composed task.
 * Between them they cover 16, 12, 12 and 27 of the 71 sets.
 *
 * An island that *can* play one spends it, ahead of a format it will get another chance at.
 * Without this they lose the rotation to the universal formats and a child meets them once or
 * twice a term — the board exists and is never seen.
 */
const SCARCE: ReadonlySet<GameId> = new Set<GameId>(['scene', 'feed', 'count', 'build']);

/**
 * Full formats from another family that a slot may borrow when its own ladder has nothing but a
 * repeat of the last island's format left. The Use slot is the one that needs it: Bridge is the
 * only use format that is full on every set (Character builder and Which scene are one or two
 * questions on most), so without a loan it plays on three islands in four. Sort by meaning
 * (words by what they are) practises a word in use rather than its sound or shape. Count is
 * left out: it is scarce, and the Read slot should keep the few sets it plays on.
 */
const BORROWABLE: Partial<Record<IslandSlot, readonly GameId[]>> = {
  use: ['sort_meaning'],
};

export interface IslandStop {
  slot: IslandSlot;
  /** The format this stop plays, or null to run `FALLBACK_MODULE[slot]` instead. */
  game: GameId | null;
  /**
   * Set on one stop per island: a second, equally full format of the same slot. The stop then
   * offers the child a choice of `game` and `alt` when it is tapped; either plays as the same
   * `island_<slot>` stop.
   */
  alt?: GameId;
}

/**
 * The orders an island's stops after the Meet can be walked in. Meet is always first and Listen
 * always comes before Use (the sentence stop leans on words the child has just heard). Consecutive
 * islands take consecutive orders, which also differ in the first stop after the Meet, so two
 * islands side by side never open the same way.
 */
export const WALK_ORDERS: readonly (readonly IslandSlot[])[] = [
  ['listen', 'read', 'pinyin', 'write', 'use'],
  ['read', 'listen', 'write', 'pinyin', 'use'],
  ['pinyin', 'listen', 'read', 'use', 'write'],
  ['listen', 'pinyin', 'read', 'use', 'write'],
  ['read', 'pinyin', 'listen', 'write', 'use'],
  ['pinyin', 'read', 'listen', 'use', 'write'],
];

export interface IslandGames {
  /** One per slot, in order — minus the write slot on a set with nothing to write. */
  stops: IslandStop[];
  /** The format the reward plays once the Challenge is passed; null for a mixed round. */
  reward: GameId | null;
}

/** A format that builds this many questions on a set may stand in for a full one, to stop a slot
 * playing the same format island after island. */
export const SEMI_GAME = 2;

const unlocked = (game: GameId, lessonOrder: number): boolean =>
  (UNLOCKED_FROM[game] ?? 1) <= lessonOrder;

/**
 * The games one island plays.
 *
 * `playable` is the set's own list from the index; `lessonOrder` gates by band; `islandIndex` is
 * the island's position in the level, and rotates each ladder so the same format does not lead
 * every island. A format is never used twice on one island.
 */
export function pickIslandGames(
  playable: readonly GameId[],
  lessonOrder: number,
  islandIndex: number,
  opts: PickIslandOptions = {},
): IslandGames {
  const pool = new Set(playable.filter((g) => unlocked(g, lessonOrder)));
  const used = new Set<GameId>();
  const stops: IslandStop[] = [];
  /** Drops the thin formats, unless nothing else is left. */
  const full = (gs: GameId[]): GameId[] => {
    const f = gs.filter((g) => (opts.sizes?.[g] ?? THIN_GAME) >= THIN_GAME);
    return f.length ? f : gs;
  };
  /** The first tier with something left once the last two islands' formats are taken out, else
   * once only the last island's are, else the first tier as it is. */
  const fresh = (tiers: GameId[][], last: GameId[], before: GameId[]): GameId[] => {
    const not = (gs: GameId[], out: GameId[]) => gs.filter((g) => !out.includes(g));
    for (const out of [[...last, ...before], last])
      for (const t of tiers) if (not(t, out).length) return not(t, out);
    return tiers[0]!;
  };
  // Read is picked after Use so that Use can borrow Sort by meaning (see BORROWABLE) before Read
  // takes it; the ladders are otherwise disjoint, so the order changes nothing else.
  const pickOrder: IslandSlot[] = [...ISLAND_SLOTS.filter((s) => s !== 'read'), 'read'];
  for (const slot of pickOrder) {
    const i = ISLAND_SLOTS.indexOf(slot);
    // A set with nothing to write has no write stop at all. It used to keep the stop and fall
    // back to the plain write module, which builds nothing for such a set: the child tapped it,
    // went straight to a nought-star result screen, and the stop marked itself done. Five stops
    // is honest; the island layout does not care how many there are.
    if (slot === 'write' && opts.hasWrite === false) continue;
    const eligible = SLOT_LADDER[slot].filter((g) => pool.has(g) && !used.has(g));
    const base = full(eligible);
    // Scarce first, then rotate: rotating by the island *and* the slot keeps two adjacent
    // islands from choosing the same way down every ladder at once.
    const scarce = base.filter((g) => SCARCE.has(g));
    const from = scarce.length ? scarce : base;
    const last = opts.recent?.[0]?.stops.find((s) => s.slot === slot)?.game;
    const before = opts.recent?.[1]?.stops.find((s) => s.slot === slot)?.game;
    let choices = from.length
      ? fresh([from, base], last ? [last] : [], before ? [before] : [])
      : [];
    // The only full format left is one this slot played on the last two islands (the Use slot,
    // where Bridge is the one format that builds everywhere): let a short one, two questions or
    // more, take the turn rather than repeat. It is topped up from the rest of the ladder.
    if (choices.length && choices.every((g) => g === last || g === before)) {
      const size = (g: GameId) => opts.sizes?.[g] ?? THIN_GAME;
      const wide = eligible.filter(
        (g) => g !== last && !choices.includes(g) && size(g) >= SEMI_GAME,
      );
      const scarceWide = wide.filter((g) => SCARCE.has(g));
      const borrowed = (BORROWABLE[slot] ?? []).filter(
        (g) => pool.has(g) && !used.has(g) && g !== last && size(g) >= THIN_GAME,
      );
      const instead = scarceWide.length ? scarceWide : wide.length ? wide : borrowed;
      if (instead.length) choices = instead;
    }
    const game = choices.length ? choices[(islandIndex + i) % choices.length]! : null;
    if (game) used.add(game);
    stops.push({ slot, game });
  }
  // The reward takes what is left over, common formats first: the scarce ones have already had
  // their chance at a stop, where they count for coverage rather than as a treat. An island's
  // last board is one it has not played, and not the one the island before it ended on.
  const spare = full(
    ISLAND_SLOTS.filter((s) => s !== 'write' || opts.hasWrite !== false)
      .flatMap((s) => SLOT_LADDER[s])
      .filter((g) => pool.has(g) && !used.has(g) && !SCARCE.has(g)),
  );
  const lastReward = opts.recent?.[0]?.reward;
  const beforeReward = opts.recent?.[1]?.reward;
  const rewards = spare.length
    ? fresh([spare], lastReward ? [lastReward] : [], beforeReward ? [beforeReward] : [])
    : [];
  const reward = rewards.length ? rewards[islandIndex % rewards.length]! : null;
  // One stop per island, rotating with the island, offers a second format to choose from. The
  // second is a full format of the same slot that no stop and not the reward is playing, so it
  // never doubles up on the island; slots with no such format are passed over.
  const choosable = stops.filter((s) => s.slot !== 'meet' && s.game);
  for (let k = 0; k < choosable.length; k++) {
    const stop = choosable[(islandIndex + k) % choosable.length]!;
    const others = SLOT_LADDER[stop.slot].filter(
      (g) =>
        pool.has(g) && !used.has(g) && g !== reward && (opts.sizes?.[g] ?? THIN_GAME) >= THIN_GAME,
    );
    if (!others.length) continue;
    const lastGame = opts.recent?.[0]?.stops.find((s) => s.slot === stop.slot)?.game;
    const fresher = others.filter((g) => g !== lastGame);
    const from = fresher.length ? fresher : others;
    stop.alt = from[(islandIndex + k) % from.length]!;
    break;
  }
  // Walk order: Meet first, the rest in this island's order (see `WALK_ORDERS`). Only the order
  // of the array changes; every stop keeps its slot, so its id and progress key are the same.
  const walk = WALK_ORDERS[islandIndex % WALK_ORDERS.length]!;
  const rank = (s: IslandStop) => (s.slot === 'meet' ? -1 : walk.indexOf(s.slot));
  stops.sort((a, b) => rank(a) - rank(b));
  return { stops, reward };
}

/**
 * A format that builds fewer questions than this on a set is thin there: played as a stop it is
 * mostly top-up from other formats (`islandRound`), so it only wins its slot when nothing fuller
 * can play.
 */
export const THIN_GAME = 3;

export interface PickIslandOptions {
  hasWrite?: boolean;
  /** Questions each format builds on this set (`set.gameSizes` in the index). A format missing
   * from the map counts as full. */
  sizes?: Readonly<Record<string, number>>;
  /** What the previous islands of the level picked, most recent first (two are used). A slot
   * avoids the format it played on the last island, and the reward avoids the last reward,
   * whenever there is anything else to choose. */
  recent?: readonly IslandGames[];
}

/**
 * The fewest questions an island stop or reward plays. Several formats build very little on some
 * sets: Which scene is one question on twelve of the fourteen sets it plays, a write format is one
 * question per write target and many sets have one, Count and Character builder are often one or
 * two. A stop of a single question reads as broken, so a short round is topped up (`islandRound`).
 */
export const MIN_STOP_QUESTIONS = 4;

/** The slot whose ladder a format belongs to, for topping up a reward round. */
export const slotOfGame = (game: GameId): IslandSlot | null =>
  ISLAND_SLOTS.find((s) => SLOT_LADDER[s].includes(game)) ?? null;

/** The most times one character may come up in a write stop. A set with a single write target
 * used to get that character from every format in turn (田 田 田 田), which a child called boring. */
export const MAX_WRITE_REPEATS = 2;

export interface IslandRoundOptions {
  /**
   * Write targets from lessons before this one, most recently taught first, for padding a write
   * stop that would otherwise repeat its own character. Other sets of the same lesson are found
   * from `ctx.bundle` and need no help; this is only for earlier *lessons*, whose bundles the
   * caller must have merged into `ctx.bundle` (`withEarlierLessons`) so their stroke data, words
   * and media are there to play. Entries with no character data in `ctx.bundle` are ignored.
   */
  earlier?: readonly CharacterEntry[];
}

/**
 * One island stop's round: the chosen format first, then — only if that came up short of
 * `MIN_STOP_QUESTIONS` — the other formats of the same slot's ladder, in ladder order, that this
 * lesson's band has opened and that build on this set. Topping up from the same family keeps the
 * stop what its label says (a listening stop stays listening) and keeps a scarce one-question
 * format like Which scene in play instead of dropping it from the sets it barely builds on.
 *
 * When the family runs out too — an early lesson has only Which scene open for its use stop — the
 * round is finished from the slot's plain module (`FALLBACK_MODULE`), the same drill the stop runs
 * when no game builds at all. An empty round stays empty: the caller runs that module in full.
 *
 * The write slot is the exception to "top up with more of the same": a write format is one
 * question per write target, so a set with one target would play that character once per format.
 * Here no character comes up more than `MAX_WRITE_REPEATS` times, and a short round is padded with
 * *review* targets the child has already met (other sets of this lesson, latest first, then
 * `opts.earlier`), interleaved so the set's own character stays first and most frequent. With no
 * such targets to borrow, the stop is allowed to stay short rather than repeat.
 */
export function islandRound(
  ctx: BuildContext,
  entries: CharacterEntry[],
  game: GameId,
  slot: IslandSlot | null = slotOfGame(game),
  opts: IslandRoundOptions = {},
): Question[] {
  const out = buildGameRound(ctx, entries, game);
  if (!slot || out.length === 0) return out;
  const order = effectiveLessonOrder(ctx.bundle.lesson.levelId, ctx.bundle.lesson.order);
  const isWrite = slot === 'write';
  const count = (qs: Question[], char: string) => qs.filter((q) => q.entry.char === char).length;
  // Only the write slot is capped; the others may legitimately ask about a character again.
  const admit = (qs: Question[], q: Question) =>
    !isWrite || count(qs, q.entry.char) < MAX_WRITE_REPEATS;
  const setId = ctx.bundle.lesson.sets.find((s) =>
    s.entries.some((e) => e.id === entries[0]?.id),
  )?.id;
  for (const g of SLOT_LADDER[slot]) {
    if (out.length >= MIN_STOP_QUESTIONS) break;
    if (g === game || !unlocked(g, order)) continue;
    for (const q of buildGameRound(ctx, entries, g)) {
      if (out.length >= MIN_STOP_QUESTIONS) break;
      if (admit(out, q)) out.push(q);
    }
  }
  if (isWrite && out.length < MIN_STOP_QUESTIONS) {
    const formats = [game, ...SLOT_LADDER.write.filter((g) => g !== game && unlocked(g, order))];
    const review = reviewQuestions(
      ctx,
      entries,
      formats,
      MIN_STOP_QUESTIONS - out.length,
      new Set(out.map((q) => q.entry.char)),
      setId,
      opts.earlier ?? [],
    );
    const mixed: Question[] = [];
    for (let i = 0; i < Math.max(out.length, review.length); i++) {
      if (out[i]) mixed.push(out[i]!);
      if (review[i]) mixed.push(review[i]!);
    }
    out.splice(0, out.length, ...mixed);
  }
  if (out.length < MIN_STOP_QUESTIONS) {
    for (const q of buildModule(ctx, FALLBACK_MODULE[slot], setId ?? null)) {
      if (out.length >= MIN_STOP_QUESTIONS) break;
      if (admit(out, q)) out.push(q);
    }
  }
  return out;
}

/**
 * Write questions about characters the child has already met, one per character, to pad a write
 * stop. Sets of this lesson before the stop's own come first (latest first), then `earlier`.
 * Each is built in the first of `formats` that plays on it — a short character has no
 * missing-stroke question — starting from a different format each time so the padding varies.
 */
function reviewQuestions(
  ctx: BuildContext,
  own: CharacterEntry[],
  formats: readonly GameId[],
  wanted: number,
  skip: Set<string>,
  setId: string | undefined,
  earlier: readonly CharacterEntry[],
): Question[] {
  const sets = ctx.bundle.lesson.sets;
  const at = sets.findIndex((s) => s.id === setId);
  const sameLesson =
    at < 0
      ? []
      : sets
          .slice(0, at)
          .reverse()
          .flatMap((s) => lessonEntries(ctx.bundle.lesson, ctx.higherChinese, s.id));
  const taken = new Set([...skip, ...own.map((e) => e.char)]);
  const out: Question[] = [];
  for (const e of [...sameLesson, ...earlier]) {
    if (out.length >= wanted) break;
    if (taken.has(e.char) || !isWriteTarget(e, ctx.higherChinese) || !ctx.bundle.characters[e.char])
      continue;
    for (let k = 0; k < formats.length; k++) {
      const q = buildGameRound(ctx, [e], formats[(out.length + 1 + k) % formats.length]!)[0];
      if (!q) continue;
      out.push(q);
      taken.add(e.char);
      break;
    }
  }
  return out;
}

/**
 * The current lesson's bundle with the character data, words and media of earlier lessons folded
 * in, plus those lessons' write targets latest-set-first, ready for `islandRound`'s `earlier`.
 * `earlier` is the bundles themselves, most recently taught lesson first. The lesson, its sets and
 * everything else stay the current lesson's own, so nothing about the stop's own questions moves.
 */
export function withEarlierLessons(
  current: LessonBundle,
  earlier: readonly LessonBundle[],
  higherChinese: boolean,
): { bundle: LessonBundle; entries: CharacterEntry[] } {
  const merge = <T>(pick: (b: LessonBundle) => Record<string, T>): Record<string, T> =>
    Object.assign({}, ...[...earlier].reverse().map(pick), pick(current));
  const entries = earlier.flatMap((b) =>
    [...b.lesson.sets]
      .reverse()
      .flatMap((s) => lessonEntries(b.lesson, higherChinese, s.id))
      .filter((e) => isWriteTarget(e, higherChinese)),
  );
  return {
    bundle: {
      ...current,
      characters: merge((b) => b.characters),
      words: merge((b) => b.words),
      media: merge((b) => b.media),
    },
    entries,
  };
}

/** The types the island Challenge falls back to in a plain screen, after the framed ones. */
const CHALLENGE_PLAIN: ExerciseType[] = [
  'audio_to_hanzi',
  'picture_to_hanzi',
  'pinyin_to_hanzi',
  'tone',
  'sentence_fill',
  'audio_to_picture',
  'trace',
];

/**
 * The island Challenge: a short boss round over the island's own characters. About
 * `CHALLENGE.islandQuestions` questions, every character at least once, with as many as the
 * characters allow presented through a game frame (tone slide, bridge, character builder, sort by
 * meaning, count, reveal) instead of a plain question screen: framed types are chosen first and
 * the frames are spread across the round so none repeats while another is unused. Scoring is the
 * strict one the Challenge always had (one try, first-try marks); a frame changes how a question
 * is answered, not how it is scored.
 *
 * The multi-word boards (Boat Forks, Bubbles, Find them all) are left out. Each is a run of
 * steps over several words rather than one first-try answer per character, so one cannot be
 * recorded here as one honest answer per character.
 */
export function buildIslandChallenge(
  ctx: BuildContext,
  entries: CharacterEntry[],
  seedTag = 'island-challenge',
): Question[] {
  if (entries.length === 0) return [];
  const rng = mulberry32(`${ctx.seed}:${seedTag}`);
  const total = Math.max(CHALLENGE.islandQuestions, entries.length);
  const sortDeck = buildGameRound(ctx, entries, 'sort_meaning');
  interface Cand {
    key: string;
    q: Question;
    framed: boolean;
  }
  const candidates = (e: CharacterEntry): Cand[] => {
    const out: Cand[] = [];
    const framed = (game: GameId, q?: Question) => {
      const built = q ?? buildGameRound(ctx, [e], game)[0];
      if (built?.frame) out.push({ key: game, q: built, framed: true });
    };
    framed('tone_slide');
    framed('bridge');
    framed('builder');
    framed('count');
    framed('reveal');
    framed(
      'sort_meaning',
      sortDeck.find((q) => q.entry.id === e.id && q.word.id === e.wordId),
    );
    for (const type of CHALLENGE_PLAIN) {
      const q = makeQuestion(ctx, rng, type, e, { review: true, trace: 'memory' });
      if (q) out.push({ key: type, q, framed: false });
    }
    return out;
  };
  const pool = new Map(entries.map((e) => [e.id, candidates(e)]));
  // A character is not asked twice by the same underlying type, framed or not (a builder and a
  // plain picture-to-hanzi are the same question).
  const taken = new Set<string>();
  const uses = new Map<string, number>();
  const out: Question[] = [];
  for (let progress = true; progress && out.length < total;) {
    progress = false;
    for (const e of shuffle(entries, rng)) {
      if (out.length >= total) break;
      let best: { c: Cand; score: number } | null = null;
      for (const c of pool.get(e.id)!) {
        if (taken.has(`${e.id}:${c.q.type}`)) continue;
        const score = (c.framed ? 0 : 100) + (uses.get(c.key) ?? 0) * 10 + rng() * 5;
        if (!best || score < best.score) best = { c, score };
      }
      if (!best) continue;
      taken.add(`${e.id}:${best.c.q.type}`);
      uses.set(best.c.key, (uses.get(best.c.key) ?? 0) + 1);
      out.push(best.c.q);
      progress = true;
    }
  }
  // Shizi names the two baskets before the first sorting card only.
  let seenSort = false;
  return spreadChars(shuffle(out, rng)).map((q) => {
    if (q.frame?.frame !== 'sort_meaning') return q;
    const intro = !seenSort;
    seenSort = true;
    return { ...q, frame: { ...q.frame, intro } };
  });
}
